import { apiService, ApiResponse } from './api';
import { LoginCredentials, LoginResponse, RefreshTokenResponse, User, Session, UserRole } from '../types/auth.types';

class AuthService {
  private readonly STORAGE_KEYS = {
    ACCESS_TOKEN: 'accessToken',
    REFRESH_TOKEN: 'refreshToken',
    USER: 'user',
  };

  // Authentication methods
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      console.log('🔐 AuthService: Attempting login with:', { email: credentials.email, rememberMe: credentials.rememberMe });
      
      // Real API call to backend
      const response = await apiService.post<{
        access_token: string;
        refresh_token: string;
        token_type: string;
        expires_in: number;
        user: {
          id: string;
          email: string;
          tenantId: string;
          firstName: string;
          lastName: string;
          avatarUrl?: string | null;
          roles: string[];
          permissions: string[];
        };
      }>('/auth/login', {
        email: credentials.email,
        password: credentials.password
      });

      // Backend returns data directly (not wrapped in response.data)
      // Check if response has 'data' property (wrapped) or direct structure
      const loginData = (response as any).access_token ? response : (response as any).data;

      // Transform API response to match our LoginResponse interface
      const loginResponse: LoginResponse = {
        accessToken: loginData.access_token,
        refreshToken: loginData.refresh_token,
        expiresIn: loginData.expires_in,
        user: {
          id: loginData.user.id,
          email: loginData.user.email,
          firstName: loginData.user.firstName,
          lastName: loginData.user.lastName,
          role: (loginData.user.roles?.[0] || 'viewer') as UserRole, // Get first role from array
          active: true, // Default to true since backend doesn't return this
          lastLoginAt: new Date().toISOString(),
          createdAt: new Date().toISOString(), // Backend doesn't return this
          avatar: loginData.user.avatarUrl,
          preferences: {
            theme: 'light',
            language: 'en',
            timezone: 'UTC',
            notifications: {
              email: true,
              sms: false,
              push: true,
              marketing: false,
            },
            dashboard: {
              defaultView: 'grid',
              itemsPerPage: 25,
              autoRefresh: true,
              refreshInterval: 30,
            },
          },
        },
      };

      // Store tokens and user data
      this.storeTokens(loginResponse.accessToken, loginResponse.refreshToken, credentials.rememberMe);
      this.storeUser(loginResponse.user, credentials.rememberMe);
      
      console.log('✅ AuthService: Login successful');
      return loginResponse;
    } catch (error) {
      console.error('❌ AuthService: Login failed:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      console.log('🔓 AuthService: Logging out user');
      
      // In a real app, call the logout endpoint
      // await apiService.post('/auth/logout');
      
      // Clear local storage
      this.clearStoredAuth();
      
      console.log('✅ AuthService: Logout successful');
    } catch (error) {
      console.error('❌ AuthService: Logout error:', error);
      // Even if the API call fails, clear local storage
      this.clearStoredAuth();
    }
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    try {
      const refreshToken = this.getStoredRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      console.log('🔄 AuthService: Refreshing access token');
      
      // Real API call to refresh token
      const response = await apiService.post<{
        access_token: string;
        token_type: string;
        expires_in: number;
      }>('/auth/refresh', {
        refresh_token: refreshToken
      });

      // Backend returns data directly (not wrapped)
      const tokenData = (response as any).access_token ? response : (response as any).data;

      // Transform response
      const refreshResponse: RefreshTokenResponse = {
        accessToken: tokenData.access_token,
        expiresIn: tokenData.expires_in,
      };

      // Store the new access token (keep same refresh token)
      // Note: We don't have the original rememberMe flag here, 
      // but storeTokens will detect which storage is currently in use.
      this.storeTokens(refreshResponse.accessToken, refreshToken);
      
      console.log('✅ AuthService: Token refresh successful');
      return refreshResponse;
    } catch (error) {
      console.error('❌ AuthService: Token refresh failed:', error);
      this.clearStoredAuth();
      throw error;
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      console.log('👤 AuthService: Fetching current user profile');
      
      // Real API call to get current user
      const response = await apiService.get<{
        id: string;
        email: string;
        tenantId: string;
        firstName: string;
        lastName: string;
        avatarUrl?: string | null;
        roles: string[];
        permissions: string[];
      }>('/auth/profile');

      // Backend returns data directly (not wrapped)
      const userData = (response as any).id ? response : (response as any).data;
      
      // Transform to our User type
      const user: User = {
        id: userData.id,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: (userData.roles?.[0] || 'viewer') as UserRole,
        active: true,
        lastLoginAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        avatar: userData.avatarUrl,
        preferences: this.getStoredUser()?.preferences || {
          theme: 'light',
          language: 'en',
          timezone: 'UTC',
          notifications: {
            email: true,
            sms: false,
            push: true,
            marketing: false,
          },
          dashboard: {
            defaultView: 'grid',
            itemsPerPage: 25,
            autoRefresh: true,
            refreshInterval: 30,
          },
        },
      };
      
      // Update stored user data with fresh data from server
      this.storeUser(user);
      
      console.log('✅ AuthService: User profile retrieved');
      return user;
    } catch (error) {
      console.error('❌ AuthService: Get current user failed:', error);
      
      // Fallback to stored user if API call fails
      const storedUser = this.getStoredUser();
      if (storedUser) {
        console.warn('⚠️ Using stored user data as fallback');
        return storedUser;
      }
      
      throw error;
    }
  }

  async updateUser(updates: Partial<User>): Promise<User> {
    try {
      console.log('👤 AuthService: Updating user profile');
      
      // In a real app:
      // const response = await apiService.put<User>('/auth/profile', updates);
      // const updatedUser = response.data;
      
      // Mock update
      const currentUser = this.getStoredUser();
      if (!currentUser) {
        throw new Error('No user data available');
      }
      
      const updatedUser = { ...currentUser, ...updates };
      // Preserve storage preference
      const isPersistent = !!localStorage.getItem(this.STORAGE_KEYS.USER);
      this.storeUser(updatedUser, isPersistent);
      
      console.log('✅ AuthService: User profile updated');
      return updatedUser;
    } catch (error) {
      console.error('❌ AuthService: Update user failed:', error);
      throw error;
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      console.log('🔒 AuthService: Changing password');
      
      // In a real app:
      // await apiService.post('/auth/change-password', { currentPassword, newPassword });
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('✅ AuthService: Password changed successfully');
    } catch (error) {
      console.error('❌ AuthService: Change password failed:', error);
      throw error;
    }
  }

  async resetPassword(email: string): Promise<void> {
    try {
      console.log('📧 AuthService: Sending password reset email');
      
      // In a real app:
      // await apiService.post('/auth/reset-password', { email });
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('✅ AuthService: Password reset email sent');
    } catch (error) {
      console.error('❌ AuthService: Reset password failed:', error);
      throw error;
    }
  }

  async getSessions(): Promise<Session[]> {
    try {
      // In a real app:
      // const response = await apiService.get<Session[]>('/auth/sessions');
      // return response.data;
      
      // Mock sessions data
      const mockSessions: Session[] = [
        {
          id: 'session-1',
          userId: 'user-1',
          device: 'Desktop',
          browser: 'Chrome 118.0',
          ipAddress: '192.168.1.100',
          location: 'Berlin, Germany',
          createdAt: new Date().toISOString(),
          lastActivityAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          isActive: true,
        },
      ];
      
      return mockSessions;
    } catch (error) {
      console.error('❌ AuthService: Get sessions failed:', error);
      throw error;
    }
  }

  async revokeSession(sessionId: string): Promise<void> {
    try {
      console.log('🚫 AuthService: Revoking session:', sessionId);
      
      // In a real app:
      // await apiService.delete(`/auth/sessions/${sessionId}`);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log('✅ AuthService: Session revoked');
    } catch (error) {
      console.error('❌ AuthService: Revoke session failed:', error);
      throw error;
    }
  }

  // Storage management methods
  private getStorage(persistent?: boolean): Storage {
    // If persistent is explicitly provided, use it
    if (persistent !== undefined) {
      return persistent ? localStorage : sessionStorage;
    }
    
    // Otherwise, check where data is currently stored
    if (localStorage.getItem(this.STORAGE_KEYS.ACCESS_TOKEN)) {
      return localStorage;
    }
    return sessionStorage;
  }

  private storeTokens(accessToken: string, refreshToken: string, persistent?: boolean): void {
    const storage = this.getStorage(persistent);
    
    // Clear other storage to avoid conflicts
    const otherStorage = storage === localStorage ? sessionStorage : localStorage;
    otherStorage.removeItem(this.STORAGE_KEYS.ACCESS_TOKEN);
    otherStorage.removeItem(this.STORAGE_KEYS.REFRESH_TOKEN);
    
    storage.setItem(this.STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    storage.setItem(this.STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    apiService.setAuthToken(accessToken);
  }

  private storeUser(user: User, persistent?: boolean): void {
    const storage = this.getStorage(persistent);
    
    // Clear other storage
    const otherStorage = storage === localStorage ? sessionStorage : localStorage;
    otherStorage.removeItem(this.STORAGE_KEYS.USER);
    
    storage.setItem(this.STORAGE_KEYS.USER, JSON.stringify(user));
  }

  private clearStoredAuth(): void {
    localStorage.removeItem(this.STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(this.STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(this.STORAGE_KEYS.USER);
    
    sessionStorage.removeItem(this.STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(this.STORAGE_KEYS.REFRESH_TOKEN);
    sessionStorage.removeItem(this.STORAGE_KEYS.USER);
    
    apiService.clearAuthToken();
  }

  getStoredAccessToken(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.ACCESS_TOKEN) || 
           sessionStorage.getItem(this.STORAGE_KEYS.ACCESS_TOKEN);
  }

  getStoredRefreshToken(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.REFRESH_TOKEN) || 
           sessionStorage.getItem(this.STORAGE_KEYS.REFRESH_TOKEN);
  }

  getStoredUser(): User | null {
    const userJson = localStorage.getItem(this.STORAGE_KEYS.USER) || 
                     sessionStorage.getItem(this.STORAGE_KEYS.USER);
    if (!userJson) return null;
    
    try {
      return JSON.parse(userJson);
    } catch (error) {
      console.error('Error parsing stored user data:', error);
      return null;
    }
  }

  isAuthenticated(): boolean {
    const accessToken = this.getStoredAccessToken();
    const user = this.getStoredUser();
    return !!(accessToken && user);
  }

  hasRole(role: string): boolean {
    const user = this.getStoredUser();
    return user?.role === role;
  }

  hasAnyRole(roles: string[]): boolean {
    const user = this.getStoredUser();
    return user ? roles.includes(user.role) : false;
  }

  // Permission checking
  canAccess(resource: string): boolean {
    const user = this.getStoredUser();
    if (!user) return false;

    // Role-based access control
    const rolePermissions: Record<string, string[]> = {
      admin: ['*'], // All resources
      manager: ['dashboard', 'bookings', 'assignments', 'fleet', 'finance', 'config'],
      dispatcher: ['dashboard', 'bookings', 'assignments', 'fleet'],
      finance: ['dashboard', 'bookings', 'finance'],
      viewer: ['dashboard'], // Read-only
    };

    const userPermissions = rolePermissions[user.role] || [];
    return userPermissions.includes('*') || userPermissions.includes(resource);
  }
}

// Create and export singleton instance
export const authService = new AuthService();
export default authService;